/**
 * BackupsPage — 备份管理列表（docs/ui_page 规范）
 * 结构：查询面板（关键词 + 操作按钮）+ 数据表（PagedGrid）+ 新建/创建备份 Dialog
 * 行点击进入详情页（single 模式）；创建备份 Dialog 用 DialogCloseTitle + InfoLabel
 */
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Switch,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Combobox,
  Option,
} from '@fluentui/react-components'
import {
  AddRegular,
  SearchRegular,
  DismissRegular,
  CloudRegular,
  DatabaseRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons'
import { backupsApi } from '@/api/backups'
import type { Backup } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatDate, formatBytes } from '@/lib/format'

const PAGE_SIZE = 10

// ── 备份类型配置 ──
const BACKUP_TYPE_LABELS: Record<string, string> = {
  full: '完整备份',
  database: '数据库',
  blobs: 'Blob 存储',
  config: '配置',
}
const BACKUP_TYPES = Object.keys(BACKUP_TYPE_LABELS)

const BACKUP_STATUS_LABELS: Record<string, string> = {
  pending: '待执行',
  running: '执行中',
  completed: '已完成',
  failed: '失败',
}

function backupTypeIcon(type: string, className: string): ReactNode {
  switch (type) {
    case 'blobs':
      return <CloudRegular className={className} />
    default:
      return <DatabaseRegular className={className} />
  }
}

// ── 样式 ──
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  queryCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-end',
  },
  keywordField: {
    flex: '2 0 240px',
    minWidth: '220px',
    flexShrink: 0,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '12px',
  },
  tableCard: {
    padding: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px 16px',
  },
  formGridItem: {
    minWidth: 0,
  },
  dialogSurface: {
    width: '480px',
    maxWidth: '90vw',
  },
  detailDialogSurface: {
    width: '65vw',
    minWidth: '600px',
    maxWidth: '1000px',
  },
})

// ── 组件 ──
export default function BackupsPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: backups, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () => backupsApi.list(),
  })

  // 查询草稿态
  const [draftQ, setDraftQ] = useState('')
  const [query, setQuery] = useState('')

  // 分页
  const [page, setPage] = useState(1)

  // 创建备份对话框状态
  const [createOpen, setCreateOpen] = useState(false)
  const [createKey, setCreateKey] = useState(0)
  const [backupType, setBackupType] = useState('full')
  const [encrypted, setEncrypted] = useState(false)

  // 详情 Dialog（行点击弹只读详情，docs/ui_page §4.2/§4.5）
  const [detailBackup, setDetailBackup] = useState<Backup | null>(null)

  // 多选（docs/ui_page §4.2：工具栏「多选」开关切换 single/multiselect）
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<Backup[]>([])

  // 下载
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const handleDownload = async (b: Backup) => {
    if (downloadingId) return
    setDownloadingId(b.id)
    try {
      await backupsApi.download(b.id)
      toast.success('已开始下载', '备份文件已开始下载')
    } catch (err) {
      toast.error('下载失败', err instanceof Error ? err.message : '请稍后重试')
    } finally {
      setDownloadingId(null)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: async (rows: Backup[]) => {
      await Promise.all(rows.map((b) => backupsApi.delete(b.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试')
    },
  })

  const openCreate = () => {
    setBackupType('full')
    setEncrypted(false)
    setCreateKey((k) => k + 1)
    setCreateOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: async (vars: { type: string; encrypted: boolean }) =>
      backupsApi.create(vars.type, vars.encrypted),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      toast.success('创建成功', '备份任务已创建')
      setCreateOpen(false)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '创建失败，请稍后重试'
      toast.error('创建失败', msg)
    },
  })

  const handleSubmit = () => {
    createMutation.mutate({ type: backupType, encrypted })
  }

  // 前端过滤
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    const list = backups ?? []
    if (!q) return list
    return list.filter(
      (b) =>
        b.id.toLowerCase().includes(q) ||
        BACKUP_TYPE_LABELS[b.backup_type]?.toLowerCase().includes(q) ||
        (b.status ?? '').toLowerCase().includes(q),
    )
  }, [backups, q])

  const hasActiveFilter = !!q
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const resetQuery = () => {
    setDraftQ('')
    setQuery('')
    setPage(1)
  }

  const applyQuery = () => {
    setQuery(draftQ)
    setPage(1)
  }

  const columns: PagedColumn<Backup>[] = [
    {
      key: 'backup_type',
      header: '类型',
      width: '140px',
      render: (b) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {backupTypeIcon(b.backup_type, '')}
          <Text weight="semibold">
            {BACKUP_TYPE_LABELS[b.backup_type] ?? b.backup_type}
          </Text>
        </div>
      ),
    },
    {
      key: 'id',
      header: '备份 ID',
      width: '200px',
      render: (b) => b.id,
    },
    {
      key: 'status',
      header: '状态',
      headerTooltip: '备份任务的执行状态',
      width: '120px',
      render: (b) => BACKUP_STATUS_LABELS[b.status] ?? b.status,
    },
    {
      key: 'file_size',
      header: '大小',
      width: '120px',
      render: (b) => formatBytes(b.file_size),
    },
    {
      key: 'encrypted',
      header: '加密',
      width: '100px',
      render: (b) => (b.encrypted ? '是' : '否'),
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '160px',
      render: (b) => formatDate(b.created_at),
    },
    {
      key: 'completed_at',
      header: '完成时间',
      width: '160px',
      render: (b) => formatDate(b.completed_at),
    },
    {
      key: 'actions',
      header: '操作',
      width: '110px',
      render: (b) => (
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowDownloadRegular />}
          disabled={b.status !== 'completed' || downloadingId === b.id}
          onClick={(e) => {
            e.stopPropagation()
            handleDownload(b)
          }}
        >
          {b.status === 'completed' ? (downloadingId === b.id ? '下载中…' : '下载') : '—'}
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* ① 查询面板 */}
      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <Field
            className={styles.keywordField}
            label={<InfoLabel info="按备份 ID、类型或状态模糊搜索，留空显示全部。">关键词</InfoLabel>}
          >
            <Input
              contentBefore={<SearchRegular />}
              contentAfter={
                draftQ ? (
                  <Button
                    appearance="transparent"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="清空"
                    onClick={() => setDraftQ('')}
                  />
                ) : undefined
              }
              placeholder="搜索备份 ID / 类型 / 状态…"
              value={draftQ}
              onChange={(_, d) => setDraftQ(d.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyQuery()
              }}
            />
          </Field>
        </div>
        {hasActiveFilter && (
          <div className={styles.filterRow}>
            已应用过滤：关键词 “{query}”
          </div>
        )}
        <div className={styles.actionsRow}>
          <Button appearance="subtle" onClick={resetQuery}>
            重置
          </Button>
          <Button appearance="primary" onClick={applyQuery}>
            查询
          </Button>
          <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
            创建备份
          </Button>
        </div>
      </Card>

      {/* ② 数据表 */}
      <Card className={styles.tableCard}>
        <PagedGrid<Backup>
          rows={pageItems}
          columns={columns}
          loading={isLoading}
          rowKey={(b) => b.id}
          total={filtered.length}
          page={safePage}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          emptyText="暂无备份数据"
          toolbarLeft={
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          }
          selectionMode={selectionMode}
          onSelectionChange={setSelectedRows}
          onDelete={(rows) => deleteMutation.mutateAsync(rows)}
          onRowClick={(b) => setDetailBackup(b)}
        />
      </Card>

      {/* ③ 创建备份 Dialog（modalType="alert"，右上角 ✕，底部仅「创建」） */}
      <Dialog
        open={createOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setCreateOpen(false)
        }}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="创建备份" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="备份的内容范围：完整备份包含数据库与 Blob 存储。">备份类型</InfoLabel>}
                  required
                >
                  <Combobox
                    key={createKey}
                    defaultSelectedOptions={[backupType]}
                    onOptionSelect={(_, d) =>
                      setBackupType(d.optionValue ?? 'full')
                    }
                  >
                    {BACKUP_TYPES.map((t) => (
                      <Option key={t} value={t}>
                        {BACKUP_TYPE_LABELS[t]}
                      </Option>
                    ))}
                  </Combobox>
                </Field>
                <Field
                  className={styles.formGridItem}
                  label={<InfoLabel info="勾选后备份文件将加密存储，防止内容泄露。">加密备份</InfoLabel>}
                >
                  <Switch
                    checked={encrypted}
                    onChange={(_, d) => setEncrypted(d.checked)}
                    label="加密备份"
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? '创建中…' : '创建'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 详情 Dialog（行点击只读详情，modalType="alert" + DialogCloseTitle，docs/ui_page §4.5） */}
      <Dialog
        open={!!detailBackup}
        onOpenChange={(_, d) => !d.open && setDetailBackup(null)}
        modalType="alert"
      >
        <DialogSurface className={styles.detailDialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="备份详情" onClose={() => setDetailBackup(null)} />
            <DialogContent>
              {detailBackup && (
                <DetailGrid key={detailBackup.id}>
                  <DetailField label="备份 ID" value={detailBackup.id} mono />
                  <DetailField label="类型" value={BACKUP_TYPE_LABELS[detailBackup.backup_type] ?? detailBackup.backup_type} />
                  <DetailField label="状态" value={BACKUP_STATUS_LABELS[detailBackup.status] ?? detailBackup.status} />
                  <DetailField label="大小" value={formatBytes(detailBackup.file_size)} />
                  <DetailField label="加密" value={detailBackup.encrypted ? '是' : '否'} />
                  <DetailField label="创建时间" value={detailBackup.created_at} />
                  <DetailField label="完成时间" value={detailBackup.completed_at} />
                  <DetailField label="错误信息" value={detailBackup.error} colSpan />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}